class GroupTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.tabsInit === "true") return;
    this.dataset.tabsInit = "true";

    this.nav = this.querySelector("[data-tabs-nav]");
    this.panels = Array.from(this.querySelectorAll("[data-tabs-panel]")).filter(
      (panel) => panel.closest("group-tabs") === this,
    );

    if (!this.nav || !this.panels.length) return;

    this.accordionMobile = this.dataset.accordionMobile === "true";
    this.accordionBreakpoint = Number(this.dataset.accordionBreakpoint || 768);
    this.accordionIconTemplate = this.querySelector(
      "[data-accordion-icon-template]",
    );

    this.mediaQuery = window.matchMedia(
      `(max-width: ${this.accordionBreakpoint - 1}px)`,
    );

    this.buildNav();
    this.triggers = Array.from(this.querySelectorAll("[data-tabs-trigger]"));

    this.buildAccordion();
    this.accordionTriggers = Array.from(
      this.querySelectorAll("[data-accordion-trigger]"),
    ).filter((trigger) => trigger.closest("group-tabs") === this);

    this.bindEvents();
    this.initState();

    this.handleResize = () => {
      this.renderMode();
      this.handleHeight();
    };

    window.addEventListener("resize", this.handleResize);

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.handleResize);
    } else {
      this.mediaQuery.addListener(this.handleResize);
    }

    this.renderMode();
    this.handleHeight();

    this.dataset.tabsReady = "true";
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);

    if (this.mediaQuery?.removeEventListener) {
      this.mediaQuery.removeEventListener("change", this.handleResize);
    } else {
      this.mediaQuery?.removeListener(this.handleResize);
    }
  }

  buildNav() {
    if (this.nav.querySelector("[data-tabs-trigger]")) return;

    this.nav.innerHTML = "";

    this.panels.forEach((panel) => {
      const key = panel.dataset.tabKey;
      const title = panel.dataset.tabTitle;

      if (!key || !title) return;

      const button = document.createElement("button");
      button.type = "button";
      button.className = this.dataset.tabsTriggerClass || "tabs__tab";
      button.dataset.tabsTrigger = "";
      button.dataset.tabKey = key;
      button.textContent = title;

      this.nav.appendChild(button);
    });
  }

  buildAccordion() {
    this.panels.forEach((panel) => {
      const key = panel.dataset.tabKey;
      const title = panel.dataset.tabTitle;

      if (!key || !title) return;

      const existingButton = this.querySelector(
        `[data-accordion-trigger][data-tab-key="${key}"]`,
      );

      if (existingButton) return;

      const button = document.createElement("button");
      button.type = "button";
      button.className = `${
        this.dataset.tabsTriggerClass || "tabs__tab"
      } tabs__accordion-trigger`;

      button.dataset.accordionTrigger = "";
      button.dataset.tabKey = key;
      button.hidden = true;

      button.innerHTML = `
        <span>${title}</span>
        <span class="tabs__accordion-icon">
          ${this.accordionIconTemplate?.innerHTML || ""}
        </span>
      `;

      panel.parentNode.insertBefore(button, panel);
    });
  }

  bindEvents() {
    this.triggers.forEach((trigger, index) => {
      const key = trigger.dataset.tabKey;
      const panel = this.panels.find((item) => item.dataset.tabKey === key);

      if (!panel) return;

      if (!panel.id) panel.id = `${this.id || "tabs"}-panel-${key}`;
      if (!trigger.id) trigger.id = `${this.id || "tabs"}-trigger-${key}`;

      trigger.setAttribute("role", "tab");
      trigger.setAttribute("aria-controls", panel.id);
      trigger.setAttribute("aria-selected", "false");
      trigger.setAttribute("tabindex", "-1");

      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", trigger.id);

      trigger.addEventListener("click", () => this.activate(key));

      trigger.addEventListener("keydown", (event) => {
        let nextIndex = null;

        if (event.key === "ArrowRight")
          nextIndex = (index + 1) % this.triggers.length;
        if (event.key === "ArrowLeft")
          nextIndex = (index - 1 + this.triggers.length) % this.triggers.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = this.triggers.length - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          const nextKey = this.triggers[nextIndex].dataset.tabKey;
          this.activate(nextKey);
          this.triggers[nextIndex].focus();
        }
      });
    });

    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const key = trigger.dataset.tabKey;
        const panel = this.panels.find((item) => item.dataset.tabKey === key);

        if (!panel) return;

        if (panel.classList.contains("is-active")) {
          panel.hidden = true;
          panel.classList.remove("is-active");
          this.syncAccordionTriggers();
        } else {
          this.activate(key);
        }
      });
    });
  }

  initState() {
    const activePanel =
      this.panels.find((panel) => panel.classList.contains("is-active")) ||
      this.panels[0];

    if (!activePanel) return;

    this.activate(activePanel.dataset.tabKey);
  }

  activate(activeKey) {
    this.triggers.forEach((trigger) => {
      const active = trigger.dataset.tabKey === activeKey;
      trigger.classList.toggle("is-active", active);
      trigger.setAttribute("aria-selected", active ? "true" : "false");
      trigger.setAttribute("tabindex", active ? "0" : "-1");
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.tabKey === activeKey;
      panel.hidden = !active;
      panel.classList.toggle("is-active", active);
    });

    this.syncAccordionTriggers();
  }

  syncAccordionTriggers() {
    this.accordionTriggers.forEach((trigger) => {
      const panel = this.panels.find(
        (item) => item.dataset.tabKey === trigger.dataset.tabKey,
      );

      const active = !!panel?.classList.contains("is-active");
      const icon = trigger.querySelector("[data-accordion-icon]");

      trigger.classList.toggle("is-active", active);
      trigger.setAttribute("aria-expanded", active ? "true" : "false");

      icon?.classList.toggle("is-open", active);
    });
  }

  renderMode() {
    const isAccordion = this.accordionMobile && this.mediaQuery.matches;

    this.classList.toggle("tabs--accordion", isAccordion);
    this.nav.hidden = isAccordion;

    this.accordionTriggers.forEach((trigger) => {
      trigger.hidden = !isAccordion;
    });

    this.syncAccordionTriggers();
  }

  handleHeight() {
    if (!this.classList.contains("tabs--height-equalized")) return;

    let maxHeight = 0;
    const width = this.getBoundingClientRect().width;

    this.panels.forEach((panel) => {
      const wasHidden = panel.hidden;
      const previousPosition = panel.style.position;
      const previousVisibility = panel.style.visibility;
      const previousDisplay = panel.style.display;
      const previousWidth = panel.style.width;
      const previousMinHeight = panel.style.minHeight;
      const previousLeft = panel.style.left;
      const previousTop = panel.style.top;

      panel.hidden = false;
      panel.style.position = "absolute";
      panel.style.visibility = "hidden";
      panel.style.display = "block";
      panel.style.left = "0";
      panel.style.top = "0";
      panel.style.width = `${width}px`;
      panel.style.minHeight = "0";

      maxHeight = Math.max(maxHeight, panel.getBoundingClientRect().height);

      panel.hidden = wasHidden;
      panel.style.position = previousPosition;
      panel.style.visibility = previousVisibility;
      panel.style.display = previousDisplay;
      panel.style.width = previousWidth;
      panel.style.minHeight = previousMinHeight;
      panel.style.left = previousLeft;
      panel.style.top = previousTop;
    });

    this.style.setProperty("--tabs-equalized-height", `${maxHeight}px`);
  }
}

if (!customElements.get("group-tabs")) {
  customElements.define("group-tabs", GroupTabs);
}
